import { getFunctions } from 'firebase/functions';
import { assign, MachineConfig, Sender } from 'xstate';
import { log } from 'xstate/lib/actions';
import submitCaseCheckoutStates, {
  CheckoutContext as SubmitCaseCheckoutContext,
  CheckoutEvent,
} from '../submitCase/checkout';
import * as RDBCases from '../../services/buildfire/rdb/cases';
import * as API from '../../api/cloudFunctions';
import { PaymentType } from '../submitCase/selectPaymentType';
import { CaseType } from '../submitCase/caseType';
import { Site } from '../../services/buildfire/rdb/cases';

export type CheckoutContext = SubmitCaseCheckoutContext & {
  case: RDBCases.Case;
};

function updateCase(context: CheckoutContext) {
  return async (send: Sender<CheckoutEvent>) => {
    const {
      caseInfo,
      firebaseApp,
      case: c,
      selectedPaymentMethod,
      userId,
      setupIntent,
      paymentType,
      selectedInvoiceAccountUid,
      caseType,
    } = context;

    try {
      const functions = getFunctions(firebaseApp);

      const {
        sites,
        submitterFirstName,
        submitterLastName,
        submitterEmailAddresses,
        patientFirstName,
        patientLastName,
        ...rest
      } = caseInfo!;

      const emailAddresses = submitterEmailAddresses?.filter(Boolean);

      const invoicingEnabled = paymentType === PaymentType.INVOICE;
      const result = await API.updateCase(functions, {
        case: {
          ...rest,
          invoiceAccount: undefined, // need to cancel this out here since added on backend
          priority: caseType === CaseType.PRIORITY,
          id: c.id,
          // Sites should already be uploaded from uploadingSites state
          sites,
          user: {
            email: c.user.email,
            displayName: c.user.displayName,
          },
          submitter: {
            first: submitterFirstName,
            last: submitterLastName,
            email: emailAddresses[0], // TODO deprecate and only use `additionalEmails`
            additionalEmails: emailAddresses?.slice(1),
          },
          patient: {
            first: patientFirstName,
            last: patientLastName,
          },
          uid: c.uid,
          billToParentAccount:
            invoicingEnabled && selectedInvoiceAccountUid !== userId,
        },
        user: {
          id: c.uid,
          name: c.user.displayName,
          email: c.user.email,
        },
        paymentMethodId: invoicingEnabled ? undefined : selectedPaymentMethod,
        setupIntentId: invoicingEnabled ? undefined : setupIntent.id,
        invoiceAccount: invoicingEnabled
          ? selectedInvoiceAccountUid
          : undefined,
      });
      return result;
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  };
}

const checkoutStates: MachineConfig<CheckoutContext, any, CheckoutEvent> = {
  ...(submitCaseCheckoutStates as any),
  states: {
    ...(submitCaseCheckoutStates.states as any),
    submitCase: {
      ...submitCaseCheckoutStates.states!.submitCase,
      invoke: {
        src: updateCase,
        onDone: {
          target: 'success',
          actions: assign({
            case: (context, ev) => ev.data.case,
          }),
        },
        onError: {
          target: 'idle',
          actions: [
            log((context, ev) => {
              console.error(ev);
            }),
            assign({
              error: (context, ev) => ev.data,
            }),
          ],
        },
      },
    },
  },
};

export default checkoutStates;
